import { useState, useMemo, useEffect } from "react";
import { useRealtimeCases, Case } from "@/lib/useRealtimeCases";
import { groupNotifications } from "@/lib/notifications";
import { useNotifications } from "@/lib/useNotifications";
import { useSelectedCenters } from "@/lib/SelectedCentersContext";
import { useBrand } from "@/lib/BrandContext";
import { useClientTypeFilter } from "@/lib/ClientTypeFilterContext";
import { detectBrandFromCenter, getCentersForBrand, mergeCenterOptions } from "@/lib/brandConfig";
import { CLIENT_TYPE_OPTIONS, normalizeClientType } from "@/lib/clientTypes";
import { orderedStatuses, statusColors } from "@/lib/statusColors";
import { CaseCard } from "@/components/kanban/CaseCard";
import { valueTier } from "@/lib/caseValue";
import { CaseDrawer } from "@/components/kanban/CaseDrawer";
import { MessagesSlidePanel } from "@/components/kanban/MessagesSlidePanel";
import { SignaturesSlidePanel } from "@/components/kanban/SignaturesSlidePanel";
import { CreateCaseModal } from "@/components/kanban/CreateCaseModal";
import { SignatureCelebration } from "@/components/SignatureCelebration";
import { TodoTodayModal } from "@/components/kanban/TodoTodayModal";
import { diffParisCalendarDays, getParisDateKey } from "@/lib/parisDateTime";
import { matchesDemandFilters, PAX_RANGES } from "@/lib/kanbanFilters";

const budgetSteps = Array.from({ length: 10 }, (_, i) => i * 1500);

export default function KanbanPage() {
  const { cases, loading, error, archivedLoaded, archivedLoading, loadArchivedCases } = useRealtimeCases();
  const [showArchived, setShowArchived] = useState(false);
  const { notifications } = useNotifications();
  const { brand } = useBrand(); // Marque sélectionnée globalement
  const { selectedClientTypes, setAvailableClientTypes } = useClientTypeFilter();
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [selectedCenter, setSelectedCenter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [demandFiltersOpen, setDemandFiltersOpen] = useState(false);
  const [paxFilter, setPaxFilter] = useState("all");
  const [budgetFilter, setBudgetFilter] = useState("all");
  const demandFilterCount = Number(paxFilter !== "all") + Number(budgetFilter !== "all");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const [messagesSlideOpen, setMessagesSlideOpen] = useState(false);
  const [signaturesSlideOpen, setSignaturesSlideOpen] = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState<"summary" | "messages" | "quotes" | "invoices" | "logs">("summary");
  
  // État des colonnes collapsées (persisté dans localStorage)
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("kanban-collapsed-columns");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Réinitialiser le centre sélectionné quand on change de marque
  useEffect(() => {
    setSelectedCenter("all");
  }, [brand]);

  // Extraire les centres uniques (filtrés par marque)
  const centers = useMemo(() => {
    const brandFilteredCases = cases.filter(c => {
      const caseBrand = c.brand || detectBrandFromCenter(c.center);
      return caseBrand === brand;
    });
    const uniqueCenters = Array.from(new Set(brandFilteredCases.map(c => c.center).filter(Boolean))) as string[];
    if (brand === "smile") {
      return mergeCenterOptions(getCentersForBrand(brand), uniqueCenters);
    }
    return uniqueCenters.sort();
  }, [cases, brand]);

  // Extraire les client_type disponibles pour le dropdown header
  const availableClientTypes = CLIENT_TYPE_OPTIONS;

  useEffect(() => {
    setAvailableClientTypes([...availableClientTypes]);
  }, [availableClientTypes, setAvailableClientTypes]);

  // Filtrer les dossiers par marque, centre et recherche
  const filteredCases = useMemo(() => {
    return cases.filter(c => {
      // Filtre par marque (utilise le champ brand ou détecte via le centre)
      const caseBrand = c.brand || detectBrandFromCenter(c.center);
      const matchesBrand = caseBrand === brand;
      
      // Filtre par centre
      const matchesCenter = selectedCenter === "all" || c.center === selectedCenter;
      
      // Filtre par recherche (company_name)
      const matchesSearch = !searchQuery || 
        c.company_name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Filtre par type de client (null => tous)
      const normalizedClientType = normalizeClientType(c.client_type);
      const matchesClientType = !selectedClientTypes
        ? true
        : !!normalizedClientType && selectedClientTypes.includes(normalizedClientType);
      
      return matchesBrand && matchesCenter && matchesSearch && matchesClientType && matchesDemandFilters(c, paxFilter, budgetFilter);
    });
  }, [cases, brand, selectedCenter, searchQuery, selectedClientTypes, paxFilter, budgetFilter]);

  // Groupes sans filtre de centre → pour les pastilles sur les CaseCards du kanban
  const { messageGroups: allMessageGroups, signatureItems: allSignatureItems } = useMemo(
    () => groupNotifications(notifications, []),
    [notifications]
  );

  const unreadCaseIds = useMemo(
    () => new Set(allMessageGroups.filter((item) => item.unreadCount > 0).map((item) => item.caseId)),
    [allMessageGroups]
  );

  const signatureCaseIds = useMemo(
    () => new Set(allSignatureItems.map((item) => item.caseId)),
    [allSignatureItems]
  );

  // Grouper les dossiers filtrés par statut
  const casesByStatus = useMemo(() => {
    return orderedStatuses.reduce((acc, status) => {
      acc[status] = filteredCases.filter((c) => c.status === status);

      // Tri spécial pour "Non traité" : demandes prioritaires (10 000 € et plus) d'abord, puis plus anciens en premier
      if (status === "Non traité") {
        acc[status].sort((a, b) =>
          Number(valueTier(b) === "priority") - Number(valueTier(a) === "priority") ||
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      }

      return acc;
    }, {} as Record<string, Case[]>);
  }, [filteredCases]);

  // Sauvegarder l'état dans localStorage
  useEffect(() => {
    localStorage.setItem("kanban-collapsed-columns", JSON.stringify(collapsedColumns));
  }, [collapsedColumns]);

  const toggleColumn = (status: string) => {
    setCollapsedColumns(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Centres sélectionnés (contexte partagé, plus de polling)
  const { selectedCenters } = useSelectedCenters();

  // Groupes filtrés par centres sélectionnés → pour les badges des boutons
  const { messageGroups: filteredMessageGroups, signatureItems: filteredSignatureItems } = useMemo(
    () => groupNotifications(notifications, selectedCenters),
    [notifications, selectedCenters]
  );

  // Calculer le nombre d'actions "À faire aujourd'hui" (filtré par centres)
  const todoCount = useMemo(() => {
    const now = new Date();
    let count = 0;

    const getDaysUntilEvent = (eventDate: string | null): number | null => {
      if (!eventDate) return null;
      return diffParisCalendarDays(now, eventDate);
    };

    const getHoursSinceCreation = (createdAt: string): number => {
      const created = new Date(createdAt);
      const diffMs = now.getTime() - created.getTime();
      return Math.floor(diffMs / (1000 * 60 * 60));
    };

    // Filtrer par centres sélectionnés
    const casesToCount = selectedCenters.length === 0 
      ? cases 
      : cases.filter(c => selectedCenters.includes(c.center || ""));

    casesToCount.forEach((c) => {
      const daysUntilEvent = getDaysUntilEvent(c.event_date);
      const hoursSinceCreation = getHoursSinceCreation(c.created_at);
      const hasClientResponse = c.last_client_response_at && c.last_quote_sent_at
        ? new Date(c.last_client_response_at) > new Date(c.last_quote_sent_at)
        : false;

      // Événement imminent sans confirmation
      if (c.status === "Devis envoyé" && daysUntilEvent !== null && daysUntilEvent >= 0 && daysUntilEvent < 3 && !hasClientResponse) {
        count++;
      }
      // Non traité >24h
      else if (c.status === "Non traité" && hoursSinceCreation > 24) {
        count++;
      }
      // Acompte en attente
      else if (c.status === "Signé" && daysUntilEvent !== null && daysUntilEvent >= 0 && daysUntilEvent <= 1) {
        count++;
      }
      // Relance téléphonique prévue aujourd'hui
      else if (c.phone_reminder_date === getParisDateKey(now)) {
        count++;
      }
      // Événements confirmés de la semaine
      else if (c.status === "Acompte reçu" && daysUntilEvent !== null && daysUntilEvent >= 0 && daysUntilEvent <= 7) {
        count++;
      }
    });

    return count;
  }, [cases, selectedCenters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Skeleton Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mt-2" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-9 w-28 bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-9 w-28 bg-gray-200 rounded-lg animate-pulse" />
                <div className="w-px h-8 bg-gray-200" />
                <div className="h-9 w-24 bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
            <div className="flex gap-2 pb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-9 w-32 bg-gray-200 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        </div>
        {/* Skeleton Columns */}
        <div className="p-6 overflow-x-auto">
          <div className="flex gap-4 min-w-max">
            {Array.from({ length: 7 }).map((_, colIdx) => (
              <div key={colIdx} className="flex-shrink-0 w-80">
                <div className="rounded-t-lg p-3 bg-gray-200 animate-pulse">
                  <div className="h-4 w-24 bg-gray-300 rounded" />
                  <div className="h-3 w-16 bg-gray-300 rounded mt-1.5" />
                </div>
                <div className="bg-gray-50 rounded-b-lg p-3 min-h-[200px] border border-t-0 border-gray-200 space-y-3">
                  {Array.from({ length: 3 + (colIdx % 3) }).map((_, cardIdx) => (
                    <div key={cardIdx} className="bg-white rounded-lg border border-gray-200 p-3 animate-pulse">
                      <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
                      <div className="h-3 w-1/2 bg-gray-100 rounded mb-3" />
                      <div className="flex gap-2 mb-2">
                        <div className="h-5 w-16 bg-gray-100 rounded" />
                        <div className="h-5 w-20 bg-gray-100 rounded" />
                      </div>
                      <div className="h-3 w-2/3 bg-gray-100 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600">Erreur : {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Kanban Commercial
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {filteredCases.length} dossier{filteredCases.length > 1 ? "s" : ""} 
                {searchQuery && " (filtrés)"}
                {selectedCenter !== "all" && ` - ${selectedCenter}`}
              </p>
            </div>

            {/* Actions à droite */}
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => setDemandFiltersOpen(v => !v)} aria-expanded={demandFiltersOpen} aria-controls="kanban-demand-filters"
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${demandFilterCount ? "border-indigo-200 bg-indigo-50 text-indigo-800" : "border-gray-300 bg-white text-gray-700"}`}>
                Filtres{demandFilterCount > 0 ? ` (${demandFilterCount})` : ""}
              </button>
              {/* Bouton Nouveaux messages */}
              <button
                onClick={() => { setMessagesSlideOpen(true); setSignaturesSlideOpen(false); }}
                className={`relative px-4 py-2 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-2 text-sm ${
                  filteredMessageGroups.length > 0
                    ? "bg-gradient-to-r from-red-500 to-orange-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                title="Voir les messages en attente"
              >
                <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="hidden sm:inline">Messages</span>
                {filteredMessageGroups.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-white/25 text-white">
                    {filteredMessageGroups.length}
                  </span>
                )}
              </button>

              {/* Bouton Nouvelles signatures */}
              <button
                onClick={() => { setSignaturesSlideOpen(true); setMessagesSlideOpen(false); }}
                className={`relative px-4 py-2 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all flex items-center gap-2 text-sm ${
                  filteredSignatureItems.length > 0
                    ? "bg-gradient-to-r from-emerald-500 to-green-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                title="Voir les signatures en attente"
              >
                <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">Signatures</span>
                {filteredSignatureItems.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-white/25 text-white">
                    {filteredSignatureItems.length}
                  </span>
                )}
              </button>

              {/* Séparateur visuel */}
              <div className="w-px h-8 bg-gray-200" />

              {/* Bouton À faire aujourd'hui */}
              <button
                onClick={() => setTodoModalOpen(true)}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2 text-sm"
                title="Voir les actions prioritaires"
              >
                <span className="text-base">🔥</span>
                <span className="hidden sm:inline">À faire</span>
                {todoCount > 0 && (
                  <span className="bg-white text-red-600 px-2 py-0.5 rounded-full text-xs font-bold">
                    {todoCount}
                  </span>
                )}
              </button>

              {/* Bouton Nouveau dossier */}
              <button
                onClick={() => setCreateModalOpen(true)}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                title="Ajouter un dossier"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {/* Barre de recherche */}
              <div className="relative w-72">
                <input
                  type="text"
                  placeholder="Rechercher une entreprise..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
                <svg 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {demandFiltersOpen && <div id="kanban-demand-filters" className="mb-4 flex flex-wrap items-end gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="text-xs font-medium text-gray-600">Nombre de participants (pax)
              <select value={paxFilter} onChange={e => setPaxFilter(e.target.value)} className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
                <option value="all">Tous les effectifs</option>
                {PAX_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                <option value="unknown">Effectif non renseigné</option>
              </select>
            </label>
            <label className="text-xs font-medium text-gray-600">Budget total de la demande
              <select value={budgetFilter} onChange={e => setBudgetFilter(e.target.value)} className="mt-1 block max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
                <option value="all">Tous les budgets</option>
                {budgetSteps.map(min => <option key={min} value={String(min)}>{min === 0 ? "Moins de 1 500 €" : min === 13500 ? "13 500 à 15 000 €" : `${min.toLocaleString("fr-FR")} à moins de ${(min + 1500).toLocaleString("fr-FR")} €`}</option>)}
                <option value="over-15000">Plus de 15 000 €</option>
                <option value="unknown">Budget non renseigné</option>
              </select>
            </label>
            {demandFilterCount > 0 && <button type="button" onClick={() => { setPaxFilter("all"); setBudgetFilter("all"); }} className="rounded-lg px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50">Réinitialiser les filtres</button>}
          </div>}
          {/* Barre : archivés + tabs centre */}
          <div className="flex items-center gap-3 pb-2">
            {/* Toggle Supprimé / Rejeté */}
            <button
              onClick={async () => {
                if (!archivedLoaded && !showArchived) {
                  await loadArchivedCases();
                }
                setShowArchived((v) => !v);
              }}
              disabled={archivedLoading}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                showArchived
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-500 border-gray-300 hover:bg-gray-50"
              }`}
              title={showArchived ? "Masquer les dossiers Supprimé / Rejeté" : "Afficher les dossiers Supprimé / Rejeté"}
            >
              {archivedLoading ? (
                <span className="flex items-center gap-1.5">
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Chargement…
                </span>
              ) : showArchived ? (
                "✕ Masquer archivés"
              ) : (
                "📦 Supprimé / Rejeté"
              )}
            </button>

            <div className="w-px h-6 bg-gray-200 flex-shrink-0" />

          {/* Tabs par centre */}
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCenter("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                selectedCenter === "all"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Tous les centres ({cases.filter(c => (c.brand || detectBrandFromCenter(c.center)) === brand).length})
            </button>
            {centers.map((center) => {
              const centerCount = cases.filter(c => c.center === center && (c.brand || detectBrandFromCenter(c.center)) === brand).length;
              return (
                <button
                  key={center}
                  onClick={() => setSelectedCenter(center)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCenter === center
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {center} ({centerCount})
                </button>
              );
            })}
          </div>
        </div>
        </div>
      </header>

      {/* Kanban Board */}
      <div className="p-6 overflow-x-auto">
        <div className="flex gap-4 min-w-max">
          {orderedStatuses
            .filter((status) => showArchived || (status !== "Supprimé" && status !== "Rejeté"))
            .map((status) => {
            const statusCases = casesByStatus[status] || [];
            const isCollapsed = collapsedColumns.includes(status);
            
            return (
              <div
                key={status}
                className={`flex-shrink-0 transition-all ${isCollapsed ? "w-16" : "w-80"}`}
              >
                {/* Column Header */}
                <div
                  className="rounded-t-lg p-3 border-b-2 flex items-center justify-between cursor-pointer"
                  style={{
                    backgroundColor: statusColors[status],
                    borderBottomColor: statusColors[status],
                  }}
                  onClick={() => toggleColumn(status)}
                >
                  <div className={isCollapsed ? "hidden" : ""}>
                    <h2 className="font-semibold text-sm text-gray-900">
                      {status}
                    </h2>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {statusCases.length} dossier{statusCases.length > 1 ? "s" : ""}
                    </p>
                  </div>
                  
                  {isCollapsed && (
                    <div className="transform -rotate-90 whitespace-nowrap text-xs font-semibold text-gray-900">
                      {status} ({statusCases.length})
                    </div>
                  )}
                  
                  <button
                    className="ml-2 text-gray-700 hover:text-gray-900 font-bold text-lg"
                    title={isCollapsed ? "Agrandir" : "Réduire"}
                  >
                    {isCollapsed ? "+" : "−"}
                  </button>
                </div>

                {/* Column Content */}
                {!isCollapsed && (
                  <div
                    className="bg-gray-50 rounded-b-lg p-3 min-h-[200px] max-h-[calc(100vh-320px)] overflow-y-auto space-y-3 border border-t-0 border-gray-200"
                  >
                    {statusCases.length === 0 ? (
                      <div className="text-center text-gray-400 text-sm py-8">
                        Aucun dossier
                      </div>
                    ) : (
                      statusCases.map((caseItem) => (
                        <CaseCard
                          key={caseItem.id}
                          case={caseItem}
                          onClick={() => setSelectedCase(caseItem)}
                          hasUnread={unreadCaseIds.has(caseItem.id)}
                          hasRecentSignature={signatureCaseIds.has(caseItem.id)}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drawer */}
      {selectedCase && (
        <CaseDrawer
          case={selectedCase}
          onClose={() => { setSelectedCase(null); setDrawerInitialTab("summary"); }}
          initialTab={drawerInitialTab}
        />
      )}

      {/* Modal Création de dossier */}
      <CreateCaseModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(caseId) => {
          // Optionnel : ouvrir automatiquement le drawer du nouveau case
          const newCase = cases.find(c => c.id === caseId);
          if (newCase) {
            setSelectedCase(newCase);
          }
        }}
      />

      {/* Modal À faire aujourd'hui */}
      <TodoTodayModal
        isOpen={todoModalOpen}
        onClose={() => setTodoModalOpen(false)}
        cases={cases}
        onCaseClick={(caseData) => {
          setSelectedCase(caseData);
          setTodoModalOpen(false);
        }}
      />

      {/* Slide panel : Nouveaux messages */}
      <MessagesSlidePanel
        isOpen={messagesSlideOpen}
        onClose={() => setMessagesSlideOpen(false)}
        onCaseClick={(caseId) => {
          const caseToOpen = cases.find((c) => c.id === caseId);
          if (caseToOpen) {
            setDrawerInitialTab("messages");
            setSelectedCase(caseToOpen);
          }
        }}
        cases={cases}
      />

      {/* Slide panel : Nouvelles signatures */}
      <SignaturesSlidePanel
        isOpen={signaturesSlideOpen}
        onClose={() => setSignaturesSlideOpen(false)}
        onCaseClick={(caseId) => {
          const caseToOpen = cases.find((c) => c.id === caseId);
          if (caseToOpen) {
            setDrawerInitialTab("quotes");
            setSelectedCase(caseToOpen);
          }
        }}
        cases={cases}
      />

      {/* Easter egg: Animation de signature 💰 */}
      <SignatureCelebration />
    </div>
  );
}
